import { ReactElement, useState, useRef, useEffect, ChangeEvent } from 'react';

import Box from '@mui/material/Box';
import ClickAwayListener from '@mui/material/ClickAwayListener';
import InputAdornment from '@mui/material/InputAdornment';
import Paper from '@mui/material/Paper';
import Popper from '@mui/material/Popper';
import { SxProps, Theme } from '@mui/material/styles';
import TextField from '@mui/material/TextField';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';

import CheckIcon from '@mui/icons-material/Check';

// Custom color palette organized in 8x8 grid
const colorPalette = [
  // Row 1: Black to white with gray variations
  ['#000000', '#1a1a1a', '#333333', '#4d4d4d', '#666666', '#808080', '#b3b3b3', '#ffffff'],
  // Row 2: Blues - optimized for distinctiveness
  ['#000080', '#0000ff', '#0066ff', '#0099ff', '#00ccff', '#00ffff', '#66ccff', '#99ddff'],
  // Row 3: Reds - optimized for distinctiveness
  ['#800000', '#cc0000', '#ff0000', '#ff3333', '#ff6666', '#ff9999', '#ffcccc', '#ffe6e6'],
  // Row 4: Greens - optimized for distinctiveness
  ['#004d00', '#008000', '#00cc00', '#00ff00', '#33ff33', '#66ff66', '#99ff99', '#ccffcc'],
  // Row 5: Purples - optimized for distinctiveness
  ['#4d004d', '#800080', '#cc00cc', '#ff00ff', '#ff33ff', '#ff66ff', '#ff99ff', '#ffccff'],
  // Row 6: Oranges - optimized for distinctiveness
  ['#cc6600', '#ff8000', '#ff9933', '#ffb366', '#ffcc99', '#ffe6cc', '#fff2e6', '#fffaf0'],
  // Row 7: Teals - optimized for distinctiveness
  ['#004d4d', '#008080', '#00cccc', '#00ffff', '#33ffff', '#66ffff', '#99ffff', '#ccffff'],
  // Row 8: Neons - optimized for distinctiveness
  ['#ff00ff', '#00ffff', '#ffff00', '#ff0080', '#80ff00', '#ff8000', '#8000ff', '#00ff80'],
];

// Human-readable color names for accessibility
const colorNames: Record<string, string> = {
  // Row 1: Grays
  '#000000': 'Black', '#1a1a1a': 'Charcoal', '#333333': 'Dark Gray', '#4d4d4d': 'Dim Gray',
  '#666666': 'Gray', '#808080': 'Silver', '#b3b3b3': 'Light Gray', '#ffffff': 'White',
  // Row 2: Blues
  '#000080': 'Navy', '#0000ff': 'Blue', '#0066ff': 'Royal Blue', '#0099ff': 'Azure',
  '#00ccff': 'Sky Blue', '#00ffff': 'Cyan', '#66ccff': 'Light Blue', '#99ddff': 'Pale Blue',
  // Row 3: Reds
  '#800000': 'Maroon', '#cc0000': 'Crimson', '#ff0000': 'Red', '#ff3333': 'Light Red',
  '#ff6666': 'Salmon', '#ff9999': 'Pink', '#ffcccc': 'Light Pink', '#ffe6e6': 'Pale Pink',
  // Row 4: Greens
  '#004d00': 'Dark Green', '#008000': 'Green', '#00cc00': 'Lime Green', '#00ff00': 'Lime',
  '#33ff33': 'Light Lime', '#66ff66': 'Mint', '#99ff99': 'Light Mint', '#ccffcc': 'Pale Green',
  // Row 5: Purples
  '#4d004d': 'Dark Purple', '#800080': 'Purple', '#cc00cc': 'Magenta', '#ff00ff': 'Fuchsia',
  '#ff33ff': 'Light Fuchsia', '#ff66ff': 'Orchid', '#ff99ff': 'Light Orchid', '#ffccff': 'Pale Purple',
  // Row 6: Oranges
  '#cc6600': 'Brown Orange', '#ff8000': 'Orange', '#ff9933': 'Light Orange', '#ffb366': 'Peach',
  '#ffcc99': 'Pale Peach', '#ffe6cc': 'Cream', '#fff2e6': 'Light Cream', '#fffaf0': 'Ivory',
  // Row 7: Teals
  '#004d4d': 'Dark Teal', '#008080': 'Teal', '#00cccc': 'Aqua', '#33ffff': 'Light Aqua',
  '#66ffff': 'Pale Aqua', '#99ffff': 'Ice Blue', '#ccffff': 'Pale Ice',
  // Row 8: Neons
  '#ffff00': 'Yellow', '#ff0080': 'Hot Pink', '#80ff00': 'Chartreuse',
  '#8000ff': 'Violet', '#00ff80': 'Neon Green',
};

function getColorName(hex: string): string {
  return colorNames[hex.toLowerCase()] || hex;
}

function getColorLabel(hex: string): string {
  const name = getColorName(hex);
  return name !== hex ? `${name} (${hex})` : hex;
}

type GratiColorPickerProps = {
  value?: string;
  defaultValue?: string;
  onChange?: (color: string) => void;
  sx?: SxProps<Theme>;
  disableClickAway?: boolean;
};

export default function GratiColorPicker(props: GratiColorPickerProps): ReactElement {
  const { value, defaultValue, onChange, sx, disableClickAway = false } = props;
  const [isOpen, setIsOpen] = useState(false);
  const [selectedColor, setSelectedColor] = useState(value || defaultValue || colorPalette[0][0]);
  const [hoveredColor, setHoveredColor] = useState<string | null>(null);
  const [hexInput, setHexInput] = useState(value || defaultValue || colorPalette[0][0]);
  const anchorRef = useRef<HTMLDivElement>(null);

  // Update selected color when value prop changes
  useEffect(() => {
    if (value !== undefined) {
      setSelectedColor(value);
      setHexInput(value);
    }
  }, [value]);

  const isValidHex = (val: string) => /^#([0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/.test(val);

  const handleHexInputChange = (e: ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value;
    setHexInput(val);
    if (isValidHex(val)) {
      const normalized = val.toLowerCase();
      setSelectedColor(normalized);
      onChange?.(normalized);
    }
  };

  const handleHexInputBlur = () => {
    if (!isValidHex(hexInput)) {
      setHexInput(selectedColor);
    }
  };

  const handleColorClick = (color: string) => {
    setSelectedColor(color);
    setHexInput(color);
    onChange?.(color);
    setIsOpen(false);
  };

  const handleToggle = () => {
    setIsOpen(!isOpen);
  };

  const handleClose = () => {
    setIsOpen(false);
  };

  const displayedColor = hoveredColor || selectedColor;

  const popperContent = (
    <Paper
      elevation={8}
      data-color-picker
      sx={{
        p: 1,
        minWidth: '12.5rem',
        backgroundColor: 'surface.container',
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 1,
        boxShadow: 8,
      }}
    >
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: 'repeat(8, 1fr)',
          gap: 0.75,
          p: 1.5,
        }}
      >
        {colorPalette.flat().map((color, index) => (
          <Box
            key={index}
            onClick={() => handleColorClick(color)}
            onMouseEnter={() => setHoveredColor(color)}
            onMouseLeave={() => setHoveredColor(null)}
            role="option"
            aria-label={getColorLabel(color)}
            aria-selected={color === selectedColor}
            sx={{
              width: '1.5rem',
              height: '1.5rem',
              borderRadius: '50%',
              backgroundColor: color,
              cursor: 'pointer',
              border: '1px solid',
              borderColor: color === selectedColor ? 'primary.main' : 'transparent',
              position: 'relative',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              '&:hover': {
                transform: 'scale(1.1)',
                boxShadow: 2,
              },
            }}
          >
            {color === selectedColor && (
              <CheckIcon
                sx={{
                  fontSize: '0.875rem',
                  color: 'white',
                  filter: 'drop-shadow(0px 1px 2px rgba(0,0,0,0.5))',
                }}
              />
            )}
          </Box>
        ))}
      </Box>
      <Typography
        variant="bodySmall"
        sx={{
          textAlign: 'center',
          px: 1.5,
          pb: 0.5,
          color: 'text.secondary',
          minHeight: '1.5em',
        }}
      >
        {getColorLabel(displayedColor)}
      </Typography>
      <Box sx={{ px: 1.5, pb: 1.5 }}>
        <TextField
          size="small"
          fullWidth
          value={hexInput}
          onChange={handleHexInputChange}
          onBlur={handleHexInputBlur}
          placeholder="#000000"
          error={!isValidHex(hexInput)}
          slotProps={{
            input: {
              startAdornment: (
                <InputAdornment position="start">
                  <Box sx={{ width: '1rem', height: '1rem', borderRadius: '50%', bgcolor: isValidHex(hexInput) ? hexInput : selectedColor, border: '1px solid', borderColor: 'divider', flexShrink: 0 }} />
                </InputAdornment>
              ),
            },
          }}
        />
      </Box>
    </Paper>
  );

  const content = (
    <Box sx={{ display: 'inline-block' }}>
      <Tooltip title={getColorLabel(selectedColor)} arrow>
        <Box
          ref={anchorRef}
          onClick={handleToggle}
          data-color-picker
          sx={{
            width: '2rem',
            height: '2rem',
            borderRadius: '50%',
            backgroundColor: selectedColor,
            border: '2px solid',
            borderColor: 'divider',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            '&:hover': {
              opacity: 0.8,
            },
            ...sx,
          }}
        />
      </Tooltip>
      <Popper
        open={isOpen}
        anchorEl={anchorRef.current}
        placement="bottom-end"
        sx={{ zIndex: 1300 }}
      >
        {popperContent}
      </Popper>
    </Box>
  );

  if (disableClickAway) {
    return content;
  }

  return <ClickAwayListener onClickAway={handleClose}>{content}</ClickAwayListener>;
}
